import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Combobox } from "./QuestionSetCombobox";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import Markdown from "@/components/Markdown";
import { QuestionSet } from "./types";
import createAxios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const FormSchema = z.object({
  name: z.string().min(1, {
    message: "Evaluation name is required",
  }),
  questionSet: z.string().min(1, {
    message: "Question set is required",
  }),
});

export const Route = createFileRoute("/dashboard/evaluate/new")({
  component: NewJob,
  validateSearch: (search) =>
    z
      .object({
        question_set_id: z.string().optional(),
      })
      .parse(search),
  loader: async () => {
    try {
      const client = await createAxios();
      const { data } = await client.get<QuestionSet[]>(
        `${import.meta.env.VITE_BACKEND_URL}/question-sets`,
      );
      return { sampleData: data || [] };
    } catch (error) {
      toast.error("Error fetching question sets:");
      throw error;
    }
  },
});

function useFetchQuestionSet(value: string) {
  const query = useQuery({
    queryKey: ["evaluate", "question-set", value],
    queryFn: async () => {
      try {
        const client = await createAxios();
        const { data } = await client.get<QuestionSet>(
          `${import.meta.env.VITE_BACKEND_URL}/question-set/${value}`,
        );
        return data;
      } catch (error) {
        toast.error("Error fetching question set:");
        throw error;
      }
    },
    enabled: !!value,
  });
  useEffect(() => {
    if (value) query.refetch();
  }, [query, value]);

  return query;
}

function NewJob() {
  const { sampleData } = Route.useLoaderData();
  const { question_set_id } = Route.useSearch();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      questionSet: question_set_id || "",
    },
  });

  const questionSet = form.watch("questionSet");
  const isValid = form.formState.isValid;

  async function createJob(data: z.infer<typeof FormSchema>) {
    try {
      const client = await createAxios();
      const response = await client.post(
        `${import.meta.env.VITE_BACKEND_URL}/evaluation`,
        {
          name: data.name,
          question_set_id: questionSet,
        },
      );
      return response.data;
    } catch (error) {
      toast.error("Error creating job:");
      throw error;
    }
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      const response = await createJob(data);
      toast.success("Evaluation started successfully");
      navigate({ to: `/dashboard/evaluate/${response.id}` });
    } catch (error) {
      toast.error("Failed to create evaluation");
      throw error;
    }
  }

  return (
    <div className="light size-full min-h-screen bg-background p-8">
      <div className="flex flex-row justify-between gap-8">
        <div className="flex w-full flex-col gap-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormLabel className="text-3xl font-bold text-foreground">
                Create New Evaluation
              </FormLabel>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter Evaluation Name"
                        className="w-64 text-foreground"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="questionSet"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel className="font-bold text-foreground">
                      Select Question Set for Evaluation
                    </FormLabel>
                    <FormControl>
                      <Combobox
                        options={sampleData
                          .filter((set) => set.id !== undefined)
                          .map((set) => ({
                            value: set.id as string,
                            label: set.name,
                          }))}
                        value={field.value}
                        setValue={(value) => field.onChange(value)}
                        placeholder="Select a question set"
                        searchBoxPlaceholder="Search question sets..."
                        searchBoxFallback="No question sets found."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col items-start space-y-4">
                <NewQuestionSet />
                <Button
                  type="submit"
                  variant="outline"
                  className={`gap-2 p-4 ${isValid ? "bg-green-500" : "cursor-not-allowed bg-gray-300"
                    }`}
                  disabled={!isValid}
                >
                  Submit <Send className="size-4" />
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <div className="w-full">
          {questionSet ? (
            <QuestionSetPreviewCard questionSet={questionSet} />
          ) : (
            <div className="flex h-[90vh] w-full flex-col items-center justify-center bg-gray-200 p-4">
              <div className="rounded-md border-2 border-dashed border-foreground p-8 text-center">
                <h1 className="text-2xl font-bold text-foreground">
                  No question set selected
                </h1>
                <p className="text-foreground">
                  Select a question set to view the preview here
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewQuestionSet() {
  const navigate = useNavigate();
  return (
    <Button
      className="w-64"
      variant={"outlinecard"}
      onClick={() => navigate({ to: "/dashboard/evaluate/question-set/new" })}
    >
      Create New Question Set
    </Button>
  );
}

function QuestionSetPreviewCard({ questionSet }: { questionSet: string }) {
  const { data: questionSetData } = useFetchQuestionSet(questionSet);

  return (
    <Card className="max-w-4xl overflow-scroll bg-gray-200 p-4">
      <CardContent className="light h-[90vh] max-w-4xl stroke-foreground text-foreground">
        {questionSetData && (
          <div className="w-full space-y-4">
            {questionSetData.questions.map((question, index) => (
              <Card key={index}>
                <CardHeader>
                  <Markdown>{`
# Question ${index + 1}
${question.question}
`}</Markdown>
                </CardHeader>
                <CardContent>
                  <Markdown>{`

## Reference Answer
${question.reference_answer}

---

## Rubrics
${question.rubrics}
`}</Markdown>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
